import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";
import { DialogActions, DialogContent, DialogTitle } from "@mui/material";
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { format, startOfDay, isAfter } from "date-fns";

import {
   ControlpanelPrimaryButton,
   ControlpanelSecondaryButton,
   GrayIconButton,
} from "../../../components/StyledButton";
import { CustomDialog } from "../../../components/StyledComponents";
import CustomDateTimePicker from "../../../components/CustomDateTimeRangePicker";
import CustomInput from "../../../components/CustomInput";
import CustomSelect from "../../../components/CustomSelect";

import { getRosterItems } from "../../../axios/RosterItem/RosterItemApi";
import CustomAutocomplete from "../../../components/CustomAutocomplete";
import { getUserOfRoster } from "../../../axios/User/UserApi";
import { updateRequestSwap } from "../../../axios/Shift/ShiftApi";
import { isAxiosError } from 'axios';
import { getListUserGroup } from "../../../axios/Group/GroupUrlApi";
import { useLoading } from "../../../context/LoadingContext";
import { fromZonedTime } from "date-fns-tz";
import { useAuth } from "../../../context/AuthContext";

const isShiftExpired = (date, shiftTime, timeZone) => {
   if (!date || !shiftTime) return false;

   const [start, end] = shiftTime.split(" - ");

   const dateStr = format(date, "yyyy-MM-dd");

   const startDateTimeStr = `${dateStr} ${start}`;
   const startUtc = fromZonedTime(startDateTimeStr, timeZone);

   const now = new Date();

   return isAfter(now, startUtc);
};

const EditShiftRequestDialog = ({ onClose, requestData, shiftsData, rosterId, handleRefreshData }) => {
   const { t } = useTranslation();
   const [userRoster, setUserRoster] = useState(null);
   const [usersData, setUsersData] = useState([]);
   const [selectedGroup, setSelectedGroup] = useState(null);
   const [userGroup, setUserGroup] = useState([]);
   const [show, hide] = useLoading();
   const [isGroupUsersLoading, setIsGroupUsersLoading] = useState(false);
   const [isPrefilling, setIsPrefilling] = useState(true);
   const { genaralSettingTimeZone } = useAuth();

   const usersDataNoGroup = useMemo(() => {
      if (!usersData) return [];

      const map = new Map();

      usersData.forEach(item => {
         if (item.userId && !map.has(item.userId)) {
            map.set(item.userId, item);
         }
      });

      return Array.from(map.values());
   }, [usersData]);

   const groupsDataOnly = useMemo(() => {
      if (!usersData) return [];

      const map = new Map();

      usersData.forEach(item => {
         if (item.groupId && !map.has(item.groupId)) {
            map.set(item.groupId, item);
         }
      });

      return Array.from(map.values());
   }, [usersData]);

   const MAX_REASON_LENGTH = 255;

   const sortedShifts = [...(shiftsData || [])].sort(
      (a, b) => b.shiftId - a.shiftId
   );

   const [formData, setFormData] = useState({
      userId: null,
      toUserId: null,

      requesterName: "",
      swapUserName: "",

      fromDate: null,
      toDate: null,

      fromShiftId: null,
      toShiftId: null,

      fromRosterItemId: null,
      toRosterItemId: null,

      toGroupId: null,
      fromGroupId: null,

      requestReason: "",
   });

   const [fromRosterItems, setFromRosterItems] = useState([]);
   const [toRosterItems, setToRosterItems] = useState([]);

   const normalizeGroupId = (id) => {
      const num = Number(id);
      return num > 0 ? num : null;
   };

   useEffect(() => {
      if (!formData.toDate) return;
      if (!formData.toUserId && !formData.toGroupId) {
         setToRosterItems([]);
         return;
      }

      const payload = {
         rosterId: requestData.rosterId,
         userId: formData.toUserId ?? null,
         groupIds: normalizeGroupId(formData.toGroupId),
         fromDate: format(formData.toDate, "yyyy-MM-dd"),
         endDate: format(formData.toDate, "yyyy-MM-dd"),
         isEnabled: true,
         isSwapShift: false
      };

      fetchRosterItem(payload, (data) => {
         setToRosterItems(data);
      });
   }, [formData.toUserId, formData.toGroupId, formData.toDate, requestData.rosterId]);

   const [swapRosterItems, setSwapRosterItems] = useState([]);
   const [isFormValid, setIsFormValid] = useState(false);

   useEffect(() => {
      const fetchUserOfRoster = async () => {
         try {
            const payload = {
               rosterId: requestData.rosterId,
               langId: localStorage.getItem("i18nextLngId"),
            };
            const response = await getUserOfRoster(payload);
            setUsersData(response.data.data || []);
         } catch (error) {
            if (error.response?.status !== 401) {
               toast.error(t("Có lỗi khi tải người dùng của roster"));
            }
         }
      };

      fetchUserOfRoster();
   }, [requestData.rosterId, t]);

   const userGroupOptions = useMemo(() => {
      return (usersDataNoGroup || []).map((u) => ({
         userId: u.userId,
         fullName: u.fullName,
      }));
   }, [usersDataNoGroup]);

   /* ================= Prefill Data ================= */

   useEffect(() => {
      if (!formData.toUserId) return;

      const source = selectedGroup == null ? userGroupOptions : userGroup;

      if (!source || source.length === 0) return;

      const found = source.find(
         (u) => (u.userId ?? u.id) === formData.toUserId
      );

      if (found && !formData.swapUserName) {
         setFormData(prev => ({
            ...prev,
            swapUserName: {
               userId: found.userId ?? found.id,
               fullName: found.fullName
            }
         }));
      }

   }, [formData.toUserId, userGroup, userGroupOptions, selectedGroup]);

   useEffect(() => {
      if (!requestData) return;
      if (usersData.length === 0) return;

      const d = requestData;
      const initialToGroupId = normalizeGroupId(d.toGroupId);

      setSelectedGroup(initialToGroupId);

      setFormData(prev => ({
         ...prev,
         requesterName: d.fullNameFrom || "",
         swapUserName: null, // chờ resolve
         userId: d.fromUserId ?? null,
         toUserId: d.toUserId ?? null,
         fromDate: d.fromDate ? new Date(d.fromDate) : null,
         toDate: d.toDate ? new Date(d.toDate) : null,
         fromShiftId: d.fromShiftId ?? null,
         toShiftId: d.toShiftId ?? null,
         fromRosterItemId: d.fromRosterItemId ?? null,
         toRosterItemId: d.toRosterItemId ?? null,
         fromGroupId: normalizeGroupId(d.fromGroupId),
         toGroupId: initialToGroupId,
         requestReason: d.requestReason || "",
      }));

      // Tắt isPrefilling sau khi set formData cơ bản
      setTimeout(() => {
         setIsPrefilling(false);
      }, 0); // chạy ngay sau render tiếp theo

   }, [requestData, usersData.length]);

   useEffect(() => {
      if (!formData.toShiftId || toRosterItems.length === 0) return;

      const hasShift = toRosterItems.some(item => item.shiftId === formData.toShiftId);

   }, [toRosterItems, formData.toShiftId]);

   /* ================= Fetch Swap Roster ================= */

   const fetchRosterItem = async (payload, setState) => {
      const res = await getRosterItems(payload);

      let data = res?.data?.data || [];

      const { userId, groupIds } = payload;

      // case 1: chỉ group
      if (groupIds && !userId) {
         data = data.filter((item) => item.groupId === groupIds);
      }

      // case 2: chỉ user
      if (userId && !groupIds) {
         data = data.filter((item) => item.userId === userId);
      }

      // case 3: user trong group
      if (userId && groupIds) {
         const filteredByUser = data.filter((item) => item.userId === userId);

         data = filteredByUser.length > 0
            ? filteredByUser
            : data.filter((item) => item.groupId === groupIds);
      }

      setState(data);
   };

   /* ================= Swap Shift Options ================= */

   const isSameUser = formData.userId && formData.userId === formData.toUserId;

   const swapShifts = isSameUser
      ? (shiftsData || []).filter(
         (shift) =>
            !isShiftExpired(
               formData.toDate,
               shift.shiftTime,
               genaralSettingTimeZone
            )
      )
      : toRosterItems.filter(
         (item) =>
            !isShiftExpired(
               formData.toDate,
               item.shiftTime,
               genaralSettingTimeZone
            )
      );

   const isNoSwapShift = swapShifts.length === 0;

   const swapShiftOptions = isNoSwapShift
      ? [{ shiftId: "", shiftName: t("common.no-shifts-available") }]
      : swapShifts;

   /* ================= Handle Change ================= */

   const handleInputChange = (e) => {
      const { name, value } = e.target;

      if (name === "requestReason" && value.length > MAX_REASON_LENGTH) return;

      if (name === "toShiftId") {
         const isSameUser =
            formData.userId && formData.userId === formData.toUserId;

         if (isSameUser) {
            setFormData((prev) => ({
               ...prev,
               toShiftId: value,
               toRosterItemId: null,
            }));
            return;
         }

         const rosterItem = toRosterItems.find(
            (item) => item.shiftId === value
         );

         setFormData((prev) => ({
            ...prev,
            toShiftId: value,
            toRosterItemId: rosterItem?.rosterItemId ?? null
         }));

         return;
      }

      if (name === "toGroup") {
         const groupId = Number(value) > 0 ? Number(value) : null;

         setSelectedGroup(groupId);

         setFormData((prev) => ({
            ...prev,
            toGroupId: groupId,

            // ❌ bỏ reset toUserId
            ...(isPrefilling
               ? {}
               : {
                  toShiftId: null,
                  toRosterItemId: null
               }
            )
         }));

         return;
      }

      setFormData((prev) => ({
         ...prev,
         [name]: value,
      }));
   };

   /* ================= Validate ================= */

   useEffect(() => {
      const isValid =
         (formData.userId != null || formData.fromGroupId != null) &&
         (formData.toUserId != null || formData.toGroupId != null) &&
         formData.fromShiftId != null &&
         formData.toShiftId != null &&
         formData.requestReason.trim() !== "";

      setIsFormValid(isValid);
   }, [formData]);

   /* ================= Submit ================= */

   const handleSubmit = async () => {
      const payload = {
         id: requestData.requestId,

         userId: formData.userId ?? formData.fromUserId,  // fromUserId (đảm bảo có)
         toUserId: formData.toUserId || requestData.toUserId,

         fromGroupId: normalizeGroupId(formData.fromGroupId),  // THÊM
         toGroupId: normalizeGroupId(formData.toGroupId),      // THÊM

         fromRosterItemId: formData.fromRosterItemId,
         toRosterItemId: formData.toRosterItemId,

         fromShiftId: formData.fromShiftId,
         toShiftId: formData.toShiftId,

         fromDate: format(formData.fromDate, "yyyy-MM-dd"),
         toDate: format(formData.toDate, "yyyy-MM-dd"),

         requestReason: formData.requestReason,

         langId: localStorage.getItem("i18nextLngId"),
      };

      try {
         await updateRequestSwap(payload);
         toast.success(t("common.edit-shift-request-successfully"));
         handleRefreshData();
         onClose();
      } catch (error) {
         toast.error(error.response.data.message);
      }
   };

   useEffect(() => {
      if (!selectedGroup) {
         setUserGroup([]);
         setIsGroupUsersLoading(false);
         return;
      }

      const fetchData = async () => {
         setIsGroupUsersLoading(true);
         show(); // loading global nếu có
         try {
            const payload = {
               group: selectedGroup,
               pageNo: 1,
               recordPerPage: 10000,
               langId: localStorage.getItem("i18nextLngId")
            };
            const response = await getListUserGroup(payload);
            setUserGroup(response.data.data || []);
         } catch (error) {
            if (error.response?.status !== 401) {
               toast.error(t(error?.response?.data.message) || t('common.network-timeout'));
            }
         } finally {
            hide();
            setIsGroupUsersLoading(false);
         }
      };

      fetchData();
   }, [selectedGroup, show, hide, t]);

   const handleCloseDialog = () => onClose();

   const groupOptionsWithNone = [
      { groupId: null, groupName: t("common.no-group") },
      ...groupsDataOnly
   ];

   const normalizedUserGroup = userGroup.map(u => ({
      userId: u.id,
      fullName: u.fullName
   }));

   useEffect(() => {
      const isSameUser =
         formData.userId && formData.userId === formData.toUserId;

      // ✅ cùng user → dùng shiftsData
      if (isSameUser) {
         if (!shiftsData?.length) return;

         const firstShift = shiftsData[0];

         setFormData((prev) => ({
            ...prev,
            toShiftId: firstShift.shiftId,
            toRosterItemId: null, // ⚠️ không có rosterItem
         }));

         return;
      }

      // ✅ khác user → dùng roster
      if (toRosterItems.length === 0) return;

      const firstItem = toRosterItems[0];

      setFormData((prev) => ({
         ...prev,
         toShiftId: firstItem.shiftId,
         toRosterItemId: firstItem.rosterItemId
      }));
   }, [toRosterItems, formData.userId, formData.toUserId, shiftsData]);

   return (
      <CustomDialog open transitionDuration={300} maxWidth={"xl"} onClose={handleCloseDialog}>
         <DialogTitle>
            <div className="flex justify-between">
               <div className="flex items-center gap-4">
                  <p className="text-xl font-semibold">{t("common.edit-request-shift")}</p>
               </div>

               <GrayIconButton onClick={handleCloseDialog}>
                  <CloseIcon />
               </GrayIconButton>
            </div>
         </DialogTitle>

         <DialogContent>
            <div className="grid grid-cols-12 md:gap-x-10 gap-y-6">

               <div className="col-span-12 md:col-span-6 mt-4">
                  <label>
                     {t("common.your-group")}
                  </label>

                  <CustomSelect
                     name="fromGroup"
                     id="fromGroup"
                     value={formData.fromGroupId}
                     onChange={handleInputChange}
                     options={groupOptionsWithNone}
                     dataType="fromGroup"
                     className="mt-2"
                     readOnly={Boolean(requestData?.fromUserId)}
                  />
               </div>

               <div className="col-span-12 md:col-span-6 mt-4">
                  <label>
                     {t("common.swap-group")}
                  </label>

                  <CustomSelect
                     name="toGroup"
                     id="toGroup"
                     value={formData.toGroupId}
                     onChange={handleInputChange}
                     options={groupOptionsWithNone}
                     dataType="toGroup"
                     className="mt-2"
                  />
               </div>

               {/* USER */}
               <div className="col-span-12 md:col-span-6">
                  <label>{t("common.staff")}</label>
                  <CustomInput value={formData.requesterName} readOnly />
               </div>

               {/* SWAP USER */}
               <div className="col-span-12 md:col-span-6">
                  <label>
                     {t("common.staff-swap")}
                     <span className="text-red-500 ml-1">*</span>
                  </label>

                  <CustomAutocomplete
                     key="swap-user-autocomplete"
                     className="min-w-60 mt-2"
                     value={formData.swapUserName}
                     options={selectedGroup == null ? userGroupOptions : normalizedUserGroup}
                     loading={isGroupUsersLoading && selectedGroup != null}
                     freeSolo
                     dataType="user"
                     placeholder={t("common.content-161")}
                     isOptionEqualToValue={(option, value) =>
                        (option.userId ?? option.id) === (value?.userId ?? value?.id)
                     }
                     getOptionLabel={(option) => option.fullName || ""}
                     onDataChange={(name, value) => {
                        setFormData(prev => ({
                           ...prev,
                           swapUserName: value,
                           toUserId: value?.userId || value?.id || null,
                           ...(isPrefilling
                              ? {}
                              : {
                                 toShiftId: null,
                                 toRosterItemId: null
                              }
                           ),
                        }));
                     }}
                  />
               </div>

               {/* CURRENT DATE */}
               <div className="col-span-12 md:col-span-6">
                  <label>{t("common.date-work")}</label>
                  <div className="mt-2">
                     <CustomDateTimePicker
                        mode="single"
                        value={formData.fromDate}
                        onChange={(newValue) =>
                           setFormData((prev) => ({ ...prev, fromDate: newValue }))
                        }
                        disableTime
                        showOnlyDate
                        disabled
                     />
                  </div>
               </div>

               {/* SWAP DATE */}
               <div className="col-span-12 md:col-span-6">
                  <label>{t("common.swap-date-work")}</label>
                  <div className="mt-2">
                     <CustomDateTimePicker
                        mode="single"
                        value={formData.toDate}
                        onChange={(newValue) =>
                           setFormData((prev) => ({ ...prev, toDate: newValue }))
                        }
                        minDate={startOfDay(new Date())}
                        disableTime
                        showOnlyDate
                     />
                  </div>
               </div>

               {/* CURRENT SHIFT */}
               <div className="col-span-12 md:col-span-6">
                  <label>{t("common.current-shift")}</label>
                  <CustomSelect
                     name="fromShiftId"
                     value={formData.fromShiftId}
                     onChange={handleInputChange}
                     options={sortedShifts}
                     dataType="shift"
                     className="mt-2"
                     readOnly
                  />
               </div>

               {/* SWAP SHIFT */}
               <div className="col-span-12 md:col-span-6">
                  <label>{t("common.swap-current-shift")}</label>
                  <CustomSelect
                     name="toShiftId"
                     value={formData.toShiftId ?? ""}  // đảm bảo luôn có giá trị
                     onChange={handleInputChange}
                     options={swapShiftOptions}
                     className="mt-2"
                     dataType="shift"
                     readOnly={isNoSwapShift}
                  />
               </div>

               {/* DESCRIPTION */}
               <div className="col-span-12">
                  <label>{t("common.swap-reason")}</label>

                  <CustomInput
                     name="requestReason"
                     value={formData.requestReason}
                     handleInputChange={handleInputChange}
                  />

                  {/* <div
                     className={`flex justify-end text-xs mt-1 ${formData.requestReason.length >= MAX_REASON_LENGTH
                        ? "text-red-500"
                        : "text-gray-500"
                        }`}
                  >
                     {formData.requestReason.length}/{MAX_REASON_LENGTH}
                  </div> */}
               </div>
            </div>
         </DialogContent>

         <DialogActions>
            <div className="flex gap-2 px-4 py-4">
               <ControlpanelSecondaryButton onClick={handleCloseDialog}>
                  <p>{t("button.close")}</p>
               </ControlpanelSecondaryButton>

               <ControlpanelPrimaryButton
                  onClick={handleSubmit}
                  disabled={!isFormValid}
               >
                  <p>{t("button.save")}</p>
               </ControlpanelPrimaryButton>
            </div>
         </DialogActions>
      </CustomDialog>
   );
};

export default EditShiftRequestDialog;